import { useEffect, useState } from "react";
import { Plus, X, CalendarX2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/DatePicker";
import { api, formatErr, fmtDay } from "@/lib/api";

export const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const DEFAULT_SLOT = { start: "09:00", end: "17:00" };
const today = () => new Date().toISOString().slice(0, 10);

export const AvailabilityView = ({ schedule, compact = false }) => {
  if (!schedule) return <p className="text-sm text-stone-500" data-testid="availability-empty">No availability set yet. Our team will coordinate times.</p>;
  const upcomingBlocked = (schedule.blocked_dates || []).filter((d) => d >= today());
  return (
    <div data-testid="availability-view">
      {schedule.timezone && <p className="mb-3 text-xs font-mono uppercase tracking-wider text-stone-500">Times in {schedule.timezone}</p>}
      <div className={`grid gap-1.5 ${compact ? "grid-cols-7" : "grid-cols-1 sm:grid-cols-7"}`}>
        {DAYS.map(([k, label]) => {
          const slots = schedule.weekly?.[k] || [];
          return (
            <div key={k} data-testid={`availability-day-${k}`} className={`rounded-md border p-2 text-center ${slots.length ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-stone-50"}`}>
              <p className="text-[11px] font-mono uppercase text-stone-500">{label.slice(0, 3)}</p>
              {slots.length ? slots.map((s, i) => <p key={i} className="text-xs font-medium text-emerald-800">{s.start}–{s.end}</p>) : <p className="text-xs text-stone-400">—</p>}
            </div>
          );
        })}
      </div>
      {upcomingBlocked.length > 0 && <p className="mt-3 flex items-center gap-1.5 text-xs text-stone-600"><CalendarX2 className="h-3.5 w-3.5" />Unavailable: {upcomingBlocked.slice(0, 8).map(fmtDay).join(", ")}</p>}
      {!compact && schedule.notes && <p className="mt-2 text-sm italic text-stone-600">{schedule.notes}</p>}
    </div>
  );
};

const DayRow = ({ k, label, slots, onChange }) => (
  <div data-testid={`avail-edit-${k}`} className="flex flex-wrap items-center gap-3 border-b border-stone-100 py-3 last:border-0">
    <label className="flex w-36 items-center gap-2 text-sm font-medium">
      <Switch data-testid={`avail-toggle-${k}`} checked={slots.length > 0} onCheckedChange={(v) => onChange(v ? [DEFAULT_SLOT] : [])} />{label}
    </label>
    <div className="flex flex-1 flex-wrap items-center gap-2">
      {slots.length === 0 && <span className="text-sm text-stone-400">Unavailable</span>}
      {slots.map((s, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input data-testid={`avail-${k}-${i}-start`} type="time" step={900} value={s.start} onChange={(e) => onChange(slots.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} className="w-28 bg-white" />
          <span className="text-stone-400">–</span>
          <Input data-testid={`avail-${k}-${i}-end`} type="time" step={900} value={s.end} onChange={(e) => onChange(slots.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} className="w-28 bg-white" />
          <button type="button" onClick={() => onChange(slots.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-600"><X className="h-4 w-4" /></button>
        </div>
      ))}
      {slots.length > 0 && <button type="button" data-testid={`avail-add-${k}`} onClick={() => onChange([...slots, { start: "18:00", end: "20:00" }])} className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900"><Plus className="h-3 w-3" />Add time</button>}
    </div>
  </div>
);

export const AvailabilityEditor = ({ initial, onSaved }) => {
  const [s, setS] = useState({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, weekly: {}, blocked_dates: [], notes: "" });
  const [block, setBlock] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (initial) setS({ blocked_dates: [], weekly: {}, notes: "", ...initial }); }, [initial]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/me/availability", s); onSaved(data); toast.success("Availability saved"); } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const addBlock = (d) => { if (d && !s.blocked_dates.includes(d)) setS({ ...s, blocked_dates: [...s.blocked_dates, d].sort() }); setBlock(""); };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <section className="rounded-md border border-stone-200 bg-white p-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1.5"><Label>Your timezone</Label><Input data-testid="avail-timezone-input" value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })} className="w-64" /></div>
          <Button data-testid="avail-save-button" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save availability"}</Button>
        </div>
        {DAYS.map(([k, label]) => <DayRow key={k} k={k} label={label} slots={s.weekly?.[k] || []} onChange={(v) => setS({ ...s, weekly: { ...s.weekly, [k]: v } })} />)}
      </section>
      <aside className="h-fit space-y-4 rounded-md border border-stone-200 bg-[#F5F5F0] p-6">
        <div className="space-y-1.5"><Label>Block out a date</Label><DatePicker testId="avail-block-date" value={block} onChange={addBlock} placeholder="Pick a date you're away" /></div>
        <div className="flex flex-wrap gap-1.5" data-testid="avail-blocked-list">
          {s.blocked_dates.length === 0 && <p className="text-xs text-stone-500">No blocked dates.</p>}
          {s.blocked_dates.map((d) => (
            <span key={d} className="flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-xs">{fmtDay(d)}
              <button onClick={() => setS({ ...s, blocked_dates: s.blocked_dates.filter((x) => x !== d) })} className="text-stone-400 hover:text-red-600"><X className="h-3 w-3" /></button></span>
          ))}
        </div>
        <div className="space-y-1.5"><Label>Notes for businesses</Label><Textarea data-testid="avail-notes-input" rows={3} value={s.notes} onChange={(e) => setS({ ...s, notes: e.target.value })} placeholder="e.g. Prefer interviews in the morning" className="bg-white" /></div>
      </aside>
    </div>
  );
};
